import type { Logger } from "@expert/logging";

import { useReactAnalytics } from "@asurion-core/eventualize-react";
import { trackFullStoryEvent } from "@expert/monitoring";
import { useCallback, useMemo } from "react";

import type { CloseGuideSource, OpenGuideSource } from "../types";

import { automaticallySelectGuideIfNeeded } from "../logic-builder";
import { useGuideSession, useGuideStore } from "../store";

interface GuideDisclosureProps {
    callSid?: string;
    logger: Logger;
    platformTicketId: string;
    sessionId: string;
}

export interface EndedSessionProps {
    endedSessionId: string;
    endedSessionCallSid?: string;
}

export const useGuideDisclosure = ({
    callSid,
    logger: loggerProp,
    platformTicketId,
    sessionId,
}: GuideDisclosureProps) => {
    const logger = useMemo(
        () => loggerProp.child({ module: "useGuideDisclosure", platformTicketId }),
        [loggerProp, platformTicketId],
    );

    const { dispatcher } = useReactAnalytics();
    const enhancedDispatcher = callSid ? dispatcher.withIdentities({ CallSid: callSid }) : dispatcher;

    const { analyticsInfo, availableGuides, currentGuideName, isGuideOpen } = useGuideSession(sessionId);

    const openGuide = useCallback(
        ({ source, sourceDetails }: OpenGuideSource, guideToOpen?: string) => {
            const { setDisableAutomaticOpen, setIsGuideOpen, setIsWaitingForNextStep, setOpenGuideSource } =
                useGuideStore.getState();
            setOpenGuideSource(sessionId, { source, sourceDetails });
            logger.info(
                { guide: guideToOpen, step: analyticsInfo.currentStepId, source, sourceDetails },
                `Solve Guide | openGuide called`,
            );

            // TODO: fix flicker [EWS-1306](https://solutonashville.atlassian.net/browse/EWS-1306)
            setIsGuideOpen(sessionId, true);

            if (source === "auto" && guideToOpen) {
                automaticallySelectGuideIfNeeded(analyticsInfo, availableGuides, guideToOpen, logger, () =>
                    setIsWaitingForNextStep(sessionId, true),
                );
            } else if (sourceDetails === "toolbox" && availableGuides.length === 1) {
                // if only one guide exists, skip GuideSelection screen and go to first step of available guide
                automaticallySelectGuideIfNeeded(analyticsInfo, availableGuides, availableGuides[0].title, logger, () =>
                    setIsWaitingForNextStep(sessionId, true),
                );
            }

            const extra = { ...analyticsInfo, guide: guideToOpen || currentGuideName };
            logger.info({ ...extra, source, sourceDetails }, "Solve Guide | launch");
            /*
            Disclaimer, the difference between LaunchGuide and OpenGuide events:
            - LaunchGuide triggers anytime the guide component itself is made visible
            - OpenGuide triggers anytime a guided flow gets selected. This includes selecting
            a flow from the GuideSelection screen or from within an in progress guided flow.
        */
            void enhancedDispatcher.dispatchBusinessEvent("Guide_LaunchGuide", {
                source,
                sourceDetails,
                ...extra,
            });
            trackFullStoryEvent("Guide_LaunchGuide", {
                source,
                sourceDetails,
                ...extra,
            });

            setDisableAutomaticOpen(sessionId, false);
        },
        [analyticsInfo, availableGuides, currentGuideName, enhancedDispatcher, logger, sessionId],
    );

    const closeGuide = useCallback(
        (source: CloseGuideSource, endedSession?: EndedSessionProps) => {
            useGuideStore.getState().setIsGuideOpen(sessionId, false);

            /*
            When guide auto-closes at the end of a call, the dispatcher has already
            updated to the new session info, extraEnhancedDispatcher ensures the
            CloseGuide event is sent with previous session info.
        */
            const extraEnhancedDispatcher = endedSession
                ? enhancedDispatcher.withIdentities({
                      SessionId: endedSession.endedSessionId,
                      CallSid: endedSession.endedSessionCallSid,
                  })
                : enhancedDispatcher;

            const extra = { ...analyticsInfo, currentGuideName, source };
            logger.info({ ...extra, source }, "Solve Guide | close");

            void extraEnhancedDispatcher.dispatchBusinessEvent("Guide_CloseGuide", {
                ...extra,
            });
            trackFullStoryEvent("Guide_CloseGuide", {
                ...extra,
            });
        },
        [analyticsInfo, currentGuideName, enhancedDispatcher, logger, sessionId],
    );

    return {
        isGuideOpen,
        openGuide,
        closeGuide,
    };
};
